<template>
  <div class="map-container">
    <div v-if="props.isReady" ref="mapElement" class="mobile-map" style="width: 100%; height: 500px;"></div>
    <div v-else class="map-placeholder">
      <!-- 지도 로딩 중 표시 -->
      <div class="loading-indicator">지도 로딩중...</div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.map-container {
  position: relative;
  width: 100%;
  height: auto;
  /* 고정 높이 대신 자동 높이로 설정 */
  overflow: hidden;
  /* 넘치는 내용 숨김 */
  margin-bottom: 0;
  /* 하단 여백 제거 */
}

.mobile-map {
  width: 100% !important;
  height: 500px !important;
  /* 적절한 높이로 설정 */
  border-radius: 4px;
  overflow: hidden;
  display: block;
}

.map-placeholder {
  width: 100%;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #ccc;
  border-radius: 4px;
  color: #777;
  background-color: #f9f9f9;
}

.loading-indicator {
  padding: 15px;
  text-align: center;
}
</style>

<script setup>
import { defineProps, defineEmits, onMounted, nextTick, ref, watch } from 'vue';

const emit = defineEmits(['map-loaded', 'map-error']);
const mapElement = ref(null);
let map = null;
let marker = null;
let infoWindow = null;

const props = defineProps({
  lat: {
    type: [String, Number],
    default: '37.5666805'
  },
  lng: {
    type: [String, Number],
    default: '126.9784147'
  },
  title: {
    type: String,
    default: '매장'
  },
  address: {
    type: String,
    default: ''
  },
  isReady: {
    type: Boolean,
    default: false
  }
});

// 재시도 횟수 추적을 위한 변수
let retryCount = 0;
const MAX_RETRIES = 3;

// 지도 초기화 함수
const initMap = async () => {
  console.log('NaverMap: 지도 초기화 시작');

  try {
    // 네이버 맵 API가 로드되었는지 확인
    if (typeof window.naver === 'undefined' || !window.naver.maps) {
      console.error('NaverMap: 네이버 맵 API가 로드되지 않았습니다.');
      emit('map-error', '네이버 맵 API 로드 실패');
      return;
    }

    if (!props.lat || !props.lng) {
      console.error('NaverMap: 위도/경도 정보가 없습니다.');
      emit('map-error', '위치 정보가 없습니다');
      return;
    }

    const lat = typeof props.lat === 'string' ? parseFloat(props.lat) : props.lat;
    const lng = typeof props.lng === 'string' ? parseFloat(props.lng) : props.lng;

    console.log('NaverMap: 지도 생성 - 좌표:', lat, lng);

    // DOM 업데이트 기다리기
    await nextTick();

    // 맵 엘리먼트 확인
    if (!mapElement.value) {
      console.error('NaverMap: 맵 엘리먼트를 찾을 수 없습니다.');
      emit('map-error', '맵 엘리먼트 없음');
      return;
    }

    // 컨테이너 크기 확인 및 재시도 로직
    if (mapElement.value.clientWidth === 0 || mapElement.value.clientHeight === 0) {
      console.warn(`NaverMap: 맵 컨테이너 크기 문제. Width: ${mapElement.value.clientWidth}, Height: ${mapElement.value.clientHeight}`);

      if (retryCount < MAX_RETRIES) {
        retryCount++;
        const delay = 200 * retryCount;

        setTimeout(() => {
          // 강제로 크기 할당
          if (mapElement.value) {
            mapElement.value.style.width = '100%';
            mapElement.value.style.height = '500px';
            mapElement.value.style.display = 'block';

            // 강제 리플로우
            void mapElement.value.offsetHeight;

            // 리사이즈 이벤트 발생
            window.dispatchEvent(new Event('resize'));

            setTimeout(() => {
              initMap(); // 재귀적으로 재시도
            }, 100);
          }
        }, delay);
        return;
      } else {
        console.error('NaverMap: 최대 재시도 횟수 초과. 지도 초기화 실패.');
        emit('map-error', '지도 초기화 실패');
        return;
      }
    }

    const latLng = new window.naver.maps.LatLng(lat, lng);

    // 지도 옵션 설정
    const mapOptions = {
      center: latLng,
      zoom: 19,
      draggable: true,
      pinchZoom: true,
      scrollWheel: false,
      scaleControl: true,
      logoControl: true,
      mapDataControl: false,
      zoomControl: true,
      mapTypeControl: false
    };

    // 지도 생성
    map = new window.naver.maps.Map(mapElement.value, mapOptions);

    // 마커 생성
    marker = new window.naver.maps.Marker({
      position: latLng,
      map: map
    });

    // 정보창 생성 (제목이 있는 경우)
    if (props.title) {
      infoWindow = new window.naver.maps.InfoWindow({
        content: `<div style="padding:10px;min-width:100px;text-align:center;font-size:14px;font-weight:bold;">${props.title}</div>`,
        maxWidth: 200,
        backgroundColor: "#fff",
        borderColor: "#ddd",
        borderWidth: 1,
        anchorSize: new window.naver.maps.Size(10, 10),
        anchorSkew: true,
        anchorColor: "#fff",
        pixelOffset: new window.naver.maps.Point(0, -15)
      });
      infoWindow.open(map, marker);
    }

    // 지도 완전 로드 후 처리
    setTimeout(() => {
      try {
        if (map) {
          map.setCenter(latLng);
          map.panTo(latLng);

          // 지도 크기 조정 트리거
          window.dispatchEvent(new Event('resize'));

          console.log('NaverMap: 지도 로드 완료');
          emit('map-loaded', true);
        }
      } catch (e) {
        console.error('NaverMap: 최종 처리 중 오류:', e);
        emit('map-error', '지도 최종 처리 중 오류');
      }
    }, 400);

  } catch (error) {
    console.error("NaverMap: 지도 초기화 중 오류 발생:", error);
    emit('map-error', error.message || '알 수 없는 지도 오류');
  }
};

// 위치 업데이트 함수
const updateLocation = (newLat, newLng) => {
  if (!map || !marker) return;

  const lat = typeof newLat === 'string' ? parseFloat(newLat) : newLat;
  const lng = typeof newLng === 'string' ? parseFloat(newLng) : newLng;
  const latLng = new window.naver.maps.LatLng(lat, lng);

  map.setCenter(latLng);
  marker.setPosition(latLng);

  if (infoWindow) {
    infoWindow.close();
    infoWindow.open(map, marker);
  }
};

// props 변경 감지
watch([() => props.lat, () => props.lng], ([newLat, newLng]) => {
  if (map && newLat && newLng) {
    updateLocation(newLat, newLng);
  }
});

// isReady 변경 감지
watch(() => props.isReady, (newVal) => {
  if (newVal) {
    nextTick(() => {
      initMap();
    });
  }
});

onMounted(() => {
  console.log('NaverMap: component mounted. isReady:', props.isReady);

  // 이벤트 버블링 방지
  const mapContainer = document.querySelector('.map-container');
  if (mapContainer) {
    mapContainer.addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
    }, true);
  }

  // isReady가 이미 true인 경우 바로 초기화
  if (props.isReady) {
    nextTick(() => {
      initMap();
    });
  }
});
</script>
